import { adminApi } from '../services/api';
import { useState, useEffect } from 'react';

interface BiasAuditResult {
  totalMatches: number;
  byGender: Record<string, number>;
  byLanguage: Record<string, number>;
  byFaith: Record<string, number>;
  byPriceTier: Record<string, number>;
  alerts: string[];
  auditedAt: string;
}

export function MatchingBiasPage() {
  const [audit, setAudit] = useState<BiasAuditResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi<BiasAuditResult | { auditedAt?: string }>('/admin/matching/bias-audit')
      .then((d) => {
        if (d.auditedAt) setAudit(d as BiasAuditResult);
        else setAudit(null);
      })
      .catch(() => setAudit(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const runAudit = async () => {
    setRunning(true);
    try {
      const data = await adminApi<BiasAuditResult>('/admin/matching/bias-audit/run', {
        method: 'POST',
      });
      setAudit(data);
    } catch {
      // keep previous audit on failure
    } finally {
      setRunning(false);
    }
  };

  const renderBreakdown = (title: string, data: Record<string, number>) => (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="mb-3 font-semibold text-gray-900">{title}</h3>
      <div className="space-y-2">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="text-gray-600">{k}</span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Matching Bias Audit</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Monthly distribution check across gender, language, faith, and price tier
          </p>
        </div>
        <button
          type="button"
          onClick={() => void runAudit()}
          disabled={running}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {running ? 'Running…' : 'Run Audit Now'}
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : !audit ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-12 text-center text-gray-500">
          No audit results yet. Run the monthly job or click Run Audit Now.
        </div>
      ) : (
        <>
          <p className="mb-4 text-sm text-gray-500">
            Last run: {new Date(audit.auditedAt).toLocaleString('en-NG')} — {audit.totalMatches}{' '}
            confirmed/completed bookings (30 days)
          </p>

          {audit.alerts.length > 0 && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h3 className="font-semibold text-amber-900">Alerts</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
                {audit.alerts.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {renderBreakdown('By Gender', audit.byGender)}
            {renderBreakdown('By Language', audit.byLanguage)}
            {renderBreakdown('By Faith', audit.byFaith)}
            {renderBreakdown('By Price Tier', audit.byPriceTier)}
          </div>
        </>
      )}
    </div>
  );
}
